import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { HeartPulse, Activity, TrendingUp, ArrowRight, UserX, UserPlus, GitBranch, ClipboardCheck, FileText, CalendarClock, RefreshCw, Clock } from "lucide-react";
import { UserAvatar } from "@/components/common/UserAvatar";
import { Panel, Skeleton, MiniEmpty, fmtDay } from "./primitives";

const HEALTH_ITEMS = [
  { key: "stalled", label: "Stalled", tone: "amber", nav: { tab: "all", status: "active" } },
  { key: "overdue_tasks", label: "Overdue Tasks", tone: "red", nav: null },
  { key: "blocked_milestones", label: "Blocked", tone: "red", nav: null },
  { key: "ready_for_review", label: "Ready for Review", tone: "green", nav: null },
  { key: "background_action", label: "Background Action", tone: "amber", nav: null },
  { key: "licensing_action", label: "Licensing Action", tone: "amber", nav: null },
];
const TONE_TXT = { red: "text-rose-600", amber: "text-amber-600", green: "text-emerald-600" };
const TONE_BG = { red: "bg-rose-50 border-rose-100", amber: "bg-amber-50 border-amber-100", green: "bg-emerald-50 border-emerald-100" };

export function PipelineHealth({ data, loading, drill, index }) {
  const items = [...HEALTH_ITEMS];
  const unassigned = data?.kpis?.unassigned ?? 0;
  return (
    <Panel title="Pipeline Health" icon={HeartPulse} testId="pipeline-health" index={index}>
      {loading && !data ? <Skeleton h="h-40" /> : (
        <div className="grid grid-cols-2 gap-2" data-testid="health-grid">
          {items.map(({ key, label, tone, nav }) => {
            const v = data?.pipeline_health?.[key] ?? 0;
            const active = v > 0;
            return (
              <button key={key} data-testid={`health-${key}`} disabled={!nav} onClick={() => nav && drill(nav)}
                className={`rounded-lg border p-2.5 text-left transition-all ${active ? TONE_BG[tone] : "border-slate-200 bg-white"} ${nav ? "hover:shadow-sm hover:-translate-y-0.5 cursor-pointer" : "cursor-default"}`}>
                <p className={`text-lg font-bold ${active ? TONE_TXT[tone] : "text-slate-300"}`}>{v}</p>
                <p className="text-[11px] text-slate-500 leading-tight">{label}</p>
              </button>
            );
          })}
          <button data-testid="health-unassigned" onClick={() => drill({ tab: "unassigned" })}
            className={`rounded-lg border p-2.5 text-left transition-all ${unassigned > 0 ? "bg-rose-50 border-rose-100" : "border-slate-200 bg-white"} hover:shadow-sm hover:-translate-y-0.5 cursor-pointer`}>
            <p className={`text-lg font-bold ${unassigned > 0 ? "text-rose-600" : "text-slate-300"}`}>{unassigned}</p>
            <p className="text-[11px] text-slate-500 leading-tight">Unassigned</p>
          </button>
        </div>
      )}
    </Panel>
  );
}

const ACT_ICON = { stage_change: GitBranch, candidate_created: UserPlus, type_change: RefreshCw, status_change: RefreshCw, interview: CalendarClock, task: ClipboardCheck, milestone: ClipboardCheck, document: FileText, reassignment: RefreshCw };
export function RecentActivity({ data, loading, navigate, index }) {
  const rows = data?.recent_activity || [];
  return (
    <Panel title="Recent Activity" icon={Activity} testId="recent-activity" index={index}>
      {loading && !data ? <Skeleton h="h-40" /> : (rows.length ? (
        <div className="space-y-3" data-testid="activity-list">
          {rows.map((e) => {
            const Icon = ACT_ICON[e.type] || Clock;
            const clickable = !!e.opportunity_id;
            return (
              <div key={e.id} className="flex items-start gap-2.5" data-testid={`activity-${e.id}`}>
                <div className="w-6 h-6 rounded-full bg-brand/10 text-brand flex items-center justify-center shrink-0 mt-0.5"><Icon className="w-3 h-3" /></div>
                <div className="min-w-0 flex-1">
                  {clickable ? (
                    <button onClick={() => navigate(`/recruiting/opportunity/${e.opportunity_id}`)} className="text-sm text-slate-700 leading-snug hover:text-brand text-left">{e.description}</button>
                  ) : <p className="text-sm text-slate-700 leading-snug">{e.description}</p>}
                  <p className="text-[11px] text-slate-400">{e.user_name ? `${e.user_name} · ` : ""}{fmtDay(e.created_at)}</p>
                </div>
              </div>
            );
          })}
        </div>
      ) : <MiniEmpty icon={Activity} title="No recent activity" description="Activity in this period will show here." testId="activity-empty" />)}
    </Panel>
  );
}

export function RecruiterWorkload({ data, loading, drill, openReassign, index, contextLabel }) {
  const reduce = useReducedMotion();
  if (!data?.team_workload) return null;
  const rows = data.team_workload;
  const maxActive = Math.max(1, ...rows.map((t) => t.active));
  const unassigned = data.kpis?.unassigned || 0;
  return (
    <Panel title="Recruiter Workload" icon={TrendingUp} testId="team-workload" index={index}
      action={unassigned > 0 ? <button className="text-xs text-brand font-medium flex items-center gap-1" data-testid="workload-reassign-open" onClick={openReassign}>Reassign Unassigned ({unassigned}) <ArrowRight className="w-3 h-3" /></button> : null}>
      {loading && !data ? <Skeleton h="h-40" /> : (
        <>
          {contextLabel && <p className="text-[11px] text-slate-400 mb-2" data-testid="workload-context">Showing: {contextLabel}</p>}
          {unassigned > 0 && (
            <button onClick={openReassign} data-testid="workload-unassigned-row"
              className="w-full mb-3 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-left hover:bg-amber-100 transition-colors">
              <UserX className="w-4 h-4 text-amber-600" />
              <span className="text-sm font-medium text-amber-800 flex-1">Unassigned ({unassigned})</span>
              <span className="text-xs text-amber-700">Assign now →</span>
            </button>
          )}
          {rows.length ? (
            <div className="overflow-x-auto tr-scroll">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
                  <th className="py-2 font-semibold">Recruiter</th><th className="py-2 font-semibold">Active</th>
                  <th className="py-2 font-semibold text-center">Needs Attention</th><th className="py-2 font-semibold text-center">Interviews</th>
                  <th className="py-2 font-semibold text-center">Ready</th></tr></thead>
                <tbody className="divide-y divide-slate-50">
                  {rows.map((t) => (
                    <tr key={t.recruiter_id} className="hover:bg-slate-50 cursor-pointer" data-testid={`workload-${t.recruiter_id}`} onClick={() => drill({ tab: "all", recruiter_id: t.recruiter_id })}>
                      <td className="py-2.5 flex items-center gap-2"><UserAvatar name={t.recruiter_name} size={26} /><span className="font-medium text-slate-700 whitespace-nowrap">{t.recruiter_name}</span></td>
                      <td className="py-2.5 pr-3">
                        <div className="flex items-center gap-2">
                          <span className="text-slate-700 font-medium w-5">{t.active}</span>
                          <div className="flex-1 min-w-[48px] h-1.5 rounded-full bg-slate-100 overflow-hidden">
                            <motion.div className="h-full rounded-full bg-brand/70" initial={reduce ? false : { width: 0 }} animate={{ width: `${Math.round((t.active / maxActive) * 100)}%` }} transition={{ duration: 0.6, ease: "easeOut" }} />
                          </div>
                        </div>
                      </td>
                      <td className={`py-2.5 text-center ${t.needs_attention ? "text-amber-600 font-medium" : "text-slate-400"}`}>{t.needs_attention}</td>
                      <td className="py-2.5 text-center text-slate-700">{t.interviews_this_week}</td>
                      <td className={`py-2.5 text-center ${t.ready_for_review ? "text-emerald-600 font-medium" : "text-slate-400"}`}>{t.ready_for_review}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <MiniEmpty icon={TrendingUp} title="No team activity" description="No recruiter workload in this scope." testId="workload-empty" />}
        </>
      )}
    </Panel>
  );
}
